/**
 * Clu's list-shaping cards, read the way the backend
 * writes them (`cleanse_api/copilot/toolkits/list_shaping.py`): a column
 * added to a list, a column's values set or filled, and a skill run on a
 * list. The card reads the approved arguments and the server's card rows
 * (`list_shaping`) as label and state: the action, the rows it reaches, the
 * list and filter, and the most it can cost.
 */
import { type IconDefinition } from '@/components/ui/app-icon';
import { iconBook, iconPen, iconTable, iconTableColumns } from '@/lib/icons';
import { listFiltersOf } from '@/components/agent-chat/list-filters.util';
import type { AgentFilterCondition } from '@/store/features/ui.feature';
import { asRecord } from '@/components/agent-chat/as-record.util';

/** The confirmed actions whose cards are drawn here. */
export const LIST_SHAPING_ACTIONS: ReadonlySet<string> = new Set([
  'add_list_column',
  'set_column_values',
  'run_list_skill',
]);

/** What a confirmed column write shows: a step with its message. A started
 *  skill shows the smart column progress card (`smart_column_started`). */
export const LIST_COLUMN_ADDED = 'list_column_added';
export const COLUMN_VALUES_SET = 'column_values_set';

/** What a list-shaping confirmation card shows: label and state only. */
export interface ListShapingCardView {
  /** The header, the action: "Run skill", "Add column". */
  title: string;
  /** The main sentence, around the rows it reaches: `Run "Employment
   *  check" on` 2 people `in "Sample list"`. */
  text: { before: string; after?: string };
  /** The confirm button: Run, Add, Fill or Set. */
  verb: string;
  /** The confirm button once clicked: Running, Adding, Filling, Setting. */
  working: string;
  /** The rows it reaches: "10 people", or "200 of 1,082 people" when a run
   *  takes only the first of the rows that match. */
  rows?: {
    label: string;
    listId?: string;
    /** The list filter that shows exactly these rows, which makes the label
     *  a link to them; absent when no filter can (rows named by id, a run
     *  that takes only some of the matches, a fill that skips filled cells),
     *  and the label is plain text. */
    filters?: AgentFilterCondition[];
  };
  /** One muted line: the filter (its lists of values counted), the values,
   *  what else it writes. */
  meta: string[];
  /** The most it can cost; absent when it is free. */
  cost?: { amount: number; upTo: boolean };
  /** The balance, only when it falls short of the cost. */
  short?: { have: number };
  /** A resolved card's step, the sentence in words: `Run "Employment
   *  check" on 2 people in "Sample list"`. */
  summary: string;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function num(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
}

function people(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'person' : 'people'}`;
}

/** A server card row's value by its label ("List", "Values", "Scope"). */
function rowValue(card: Record<string, unknown>, label: string) {
  for (const raw of Array.isArray(card.rows) ? card.rows : []) {
    const row = asRecord(raw);
    if (row?.label === label) return text(row.value);
  }
  return undefined;
}

/**
 * The list filter that shows the rows a card reaches: the approved
 * conditions, or none (the whole list) to clear it. Rows named by id have
 * no filter that shows them.
 */
function rowFilters(
  args: Record<string, unknown>
): AgentFilterCondition[] | undefined {
  if (Array.isArray(args.lead_ids)) return undefined;
  if (args.conditions) return listFiltersOf(args.conditions);
  return [];
}

/**
 * The rows a run's filter matched, before the run took the first of them:
 * the card's `matched`, or the leading count of its "Rows" row ("1,082 rows
 * of 5,000 where ...") from a server that doesn't send it.
 */
function matchedCount(card: Record<string, unknown>): number | undefined {
  const matched = num(card.matched);
  if (matched !== undefined) return matched;
  const words = rowValue(card, 'Rows');
  const lead = words?.match(/^([\d,]+)\s+rows?\b/);
  return lead ? Number(lead[1].replace(/,/g, '')) : undefined;
}

/**
 * The filter in words with each list of values counted, not spelled out:
 * "Test ID is any of 10 values". The rows link shows the rows themselves.
 * The server writes a list as its values joined by ", ".
 */
function whereSummary(
  where: string | undefined,
  conditions: unknown
): string | undefined {
  if (!where) return undefined;
  const group = asRecord(conditions);
  let summary = where;
  for (const raw of Array.isArray(group?.conditions) ? group.conditions : []) {
    const value = text(asRecord(raw)?.value);
    if (!value?.includes(',')) continue;
    const values = value.split(',');
    const count = values.filter((part) => part.trim()).length;
    summary = summary.replace(
      ` ${values.join(', ')}`,
      ` ${count.toLocaleString()} values`
    );
  }
  return summary;
}

/** The card's header, the action: also the title of a card the server
 *  sent without its card view. */
export function listShapingTitle(
  action: string | undefined,
  mode?: unknown
): string {
  if (action === 'run_list_skill') return 'Run skill';
  if (action === 'set_column_values')
    return mode === 'fill' ? 'Fill column' : 'Set column';
  return 'Add column';
}

const WORKING: Record<string, string> = {
  Run: 'Running',
  Add: 'Adding',
  Fill: 'Filling',
  Set: 'Setting',
};

/** The card of an `add_list_column`, `set_column_values` or
 *  `run_list_skill` confirmation, built from its approved arguments and the
 *  server's card rows, or null for any other (or one without the server's
 *  card view, which then reads as its description). */
export function listShapingCardView(
  request: { tool_name?: string } & Record<string, unknown>
): ListShapingCardView | null {
  const action = request.tool_name ?? '';
  if (!LIST_SHAPING_ACTIONS.has(action)) return null;
  const card = asRecord(request.list_shaping);
  const serverTitle = text(card?.title);
  if (!card || !serverTitle) return null;
  const args = asRecord(request.arguments) ?? {};
  const listId = text(args.list_id);
  const listName = rowValue(card, 'List');
  const where = whereSummary(text(args.where), args.conditions);
  const count = num(args.row_count);
  // A run may take only the first of the rows its filter matched.
  const matched = action === 'run_list_skill' ? matchedCount(card) : undefined;
  const partOf =
    count !== undefined && matched !== undefined && matched > count
      ? matched
      : undefined;
  // A fill writes only the empty cells of the rows its filter matches.
  const fill = action === 'set_column_values' && args.mode === 'fill';
  const filters =
    listId && partOf === undefined && !fill ? rowFilters(args) : undefined;
  const rows =
    count !== undefined
      ? {
          label:
            partOf !== undefined
              ? `${count.toLocaleString()} of ${people(partOf)}`
              : people(count),
          ...(listId ? { listId } : {}),
          ...(filters ? { filters } : {}),
        }
      : undefined;
  const list = listName ? `"${listName}"` : undefined;

  let actionWords = serverTitle;
  // The word before the rows, and before the list when no rows are named.
  let onRows = 'on';
  let onList = 'in';
  let verb = 'Confirm';
  let meta: Array<string | undefined> = [];
  if (action === 'run_list_skill') {
    const skill = text(args.skill_name) ?? serverTitle.replace(/^Run /, '');
    actionWords = `Run "${skill}"`;
    onList = 'on';
    verb = 'Run';
    const scope = rowValue(card, 'Scope');
    meta = [where, scope ? `Scope ${scope}` : undefined];
  } else if (action === 'add_list_column') {
    const name = text(args.name);
    if (name) actionWords = `Add "${name}"`;
    onRows = 'to';
    onList = 'to';
    verb = 'Add';
    meta = [rowValue(card, 'Values')];
  } else if (action === 'set_column_values') {
    const column = text(args.column);
    const value = text(args.value);
    if (column && value) {
      actionWords = fill
        ? `Fill "${column}" with "${value}"`
        : `Set "${column}" to "${value}"`;
    }
    verb = fill ? 'Fill' : 'Set';
    const also = rowValue(card, 'Also changes');
    meta = [
      where,
      fill ? 'Only empty cells' : undefined,
      also ? `Also changes ${also}` : undefined,
    ];
  }
  const sentence = rows
    ? {
        before: `${actionWords} ${onRows}`,
        ...(list ? { after: `in ${list}` } : {}),
      }
    : {
        before: actionWords,
        ...(list ? { after: `${onList} ${list}` } : {}),
      };

  const amount = num(request.credits_required) ?? 0;
  const cost =
    amount > 0
      ? { amount, upTo: /^up to/i.test(text(card.total) ?? '') }
      : undefined;
  const balance = num(request.credits_available);
  return {
    title: listShapingTitle(action, args.mode),
    text: sentence,
    verb,
    working: WORKING[verb] ?? 'Confirming',
    ...(rows ? { rows } : {}),
    meta: meta.filter((part): part is string => Boolean(part)),
    ...(cost ? { cost } : {}),
    ...(cost && balance !== undefined && balance < cost.amount
      ? { short: { have: balance } }
      : {}),
    summary: [sentence.before, rows?.label, sentence.after]
      .filter(Boolean)
      .join(' '),
  };
}

/** The card's icon: a column, a pen for values, a book for a skill. */
export function listShapingIcon(action: string | undefined): IconDefinition {
  if (action === 'set_column_values') return iconPen;
  if (action === 'run_list_skill') return iconBook;
  return iconTableColumns;
}

/** The list-shaping tools as steps in the chat: the read, each request (its
 *  card is the answer), and each confirmed action under its own name. */
export const LIST_SHAPING_TOOL_STEPS: Record<
  string,
  { done: string; running: string; icon: IconDefinition }
> = {
  get_list_columns: {
    done: "Read the list's columns",
    running: "Reading the list's columns",
    icon: iconTable,
  },
  request_add_list_column: {
    done: 'Prepared a column',
    running: 'Preparing a column',
    icon: iconTableColumns,
  },
  request_set_column_values: {
    done: 'Prepared the values',
    running: 'Preparing the values',
    icon: iconPen,
  },
  request_run_list_skill: {
    done: 'Prepared the run',
    running: 'Preparing the run',
    icon: iconBook,
  },
  add_list_column: {
    done: 'Added a column',
    running: 'Adding a column',
    icon: iconTableColumns,
  },
  set_column_values: {
    done: 'Set the values',
    running: 'Setting the values',
    icon: iconPen,
  },
  run_list_skill: {
    done: 'Started a skill',
    running: 'Starting a skill',
    icon: iconBook,
  },
};
